import { useEffect, useMemo, useState } from 'react'
import { BarChart3, ChevronDown, ChevronUp, Star } from 'lucide-react'
import { getBookLogo, getTeamDisplayName, getTeamLogo } from '../lib/logoMaps'
import { classifyTotalSelection, selectionMatchesTeam } from '../lib/oddsNormalization'

function formatOdds(american, format) {
  if (american == null) return '—'
  if (format === 'decimal') {
    const dec = american > 0 ? (american / 100) + 1 : (100 / Math.abs(american)) + 1
    return dec.toFixed(2)
  }
  return american > 0 ? `+${american}` : `${american}`
}

function averageOdds(oddsArr) {
  const valid = oddsArr.filter((value) => value != null)
  if (valid.length === 0) return null
  return Math.round(valid.reduce((sum, value) => sum + value, 0) / valid.length)
}

function timeUntil(dateStr) {
  if (!dateStr) return ''
  const diff = new Date(dateStr) - Date.now()
  if (diff < 0) return ''
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ${Math.floor((diff % 3600000) / 60000)}m`
  return `${Math.floor(diff / 86400000)}d`
}

function formatLine(handicap, market, selection) {
  if (handicap == null) return ''
  if (market === 'TOTAL') {
    const prefix = String(selection || '').toLowerCase().includes('over') ? 'O' : 'U'
    return `${prefix} ${Math.abs(handicap)}`
  }
  if (handicap === 0) return 'PK'
  return handicap > 0 ? `+${handicap}` : `${handicap}`
}

function formatStartTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

function mergeBooks(primary = {}, secondary = {}) {
  return { ...primary, ...secondary }
}

function mergeRowMeta(primary = {}, secondary = {}) {
  return {
    canonical_ids: Array.from(new Set([...(primary.canonical_ids || []), ...(secondary.canonical_ids || [])])),
    selection_variants: Array.from(new Set([...(primary.selection_variants || []), ...(secondary.selection_variants || [])])),
  }
}

function getBookNames(books) {
  return new Set(Object.keys(books || {}))
}

function getCoverageStats(firstBooks = {}, secondBooks = {}) {
  const first = getBookNames(firstBooks)
  const second = getBookNames(secondBooks)
  return {
    bothSidesCount: [...first].filter((book) => second.has(book)).length,
    totalBooks: first.size + second.size,
  }
}

function createRow(selection, books, handicap, market, meta = {}) {
  return {
    selection,
    books,
    handicap,
    market_normalized: market,
    canonical_ids: meta.canonical_ids || [],
    selection_variants: meta.selection_variants || [selection],
  }
}

function createPlaceholderRow(selection, handicap, market, meta = {}) {
  return createRow(selection, {}, handicap ?? null, market, {
    canonical_ids: meta.canonical_ids || [],
    selection_variants: meta.selection_variants || [selection],
  })
}

function createChartContext(row, game) {
  return {
    canonicalId: game.canonical_id,
    canonicalIds: row.canonical_ids?.length ? row.canonical_ids : game.canonical_ids || [game.canonical_id],
    selection: row.selection,
    selectionVariants: row.selection_variants?.length ? row.selection_variants : [row.selection],
    market: row.market_normalized,
    handicap: row.handicap,
    currentBooks: row.books || {},
    currentTimestamp: game.last_updated_at || game.start_time || new Date().toISOString(),
  }
}

function formatSelectionLabel(row, game) {
  if (row.market_normalized === 'TOTAL') {
    const side = classifyTotalSelection(row.selection)
    if (side === 'over') return 'Over'
    if (side === 'under') return 'Under'
  }

  if (selectionMatchesTeam(row.selection, game.away_team, game.league)) return game.away_team
  if (selectionMatchesTeam(row.selection, game.home_team, game.league)) return game.home_team
  return row.selection
}

function buildSelectionStats(markets) {
  const stats = {}
  markets.forEach((market) => {
    Object.entries(market.selections).forEach(([selection, books]) => {
      const values = Object.values(books).filter((value) => value != null)
      const best = values.length > 0 ? Math.max(...values) : null
      const bestBook = best != null ? Object.entries(books).find(([, value]) => value === best)?.[0] || null : null
      stats[`${market.market_normalized}|${market.handicap ?? ''}|${selection}`] = {
        best,
        bestBook,
        avg: averageOdds(values),
      }
    })
  })
  return stats
}

function buildMoneylineFamily(markets, game) {
  let awayRow = null
  let homeRow = null
  const orphanRows = []
  markets.filter((market) => market.market_normalized === 'ML').forEach((market) => {
    Object.entries(market.selections).forEach(([selection, books]) => {
      const meta = market.selectionMeta?.[selection] || {}
      if (selectionMatchesTeam(selection, game.away_team, game.league)) {
        awayRow = awayRow
          ? {
              ...awayRow,
              books: mergeBooks(awayRow.books, books),
              ...mergeRowMeta(awayRow, meta),
            }
          : createRow(selection, books, null, 'ML', meta)
        return
      }

      if (selectionMatchesTeam(selection, game.home_team, game.league)) {
        homeRow = homeRow
          ? {
              ...homeRow,
              books: mergeBooks(homeRow.books, books),
              ...mergeRowMeta(homeRow, meta),
            }
          : createRow(selection, books, null, 'ML', meta)
        return
      }

      orphanRows.push(createRow(selection, books, null, 'ML', meta))
    })
  })

  if (!awayRow && !homeRow && orphanRows.length === 0) return []

  const canonicalIds = game.canonical_ids || [game.canonical_id].filter(Boolean)
  const mainRows = [
    awayRow || createPlaceholderRow(game.away_team, null, 'ML', { canonical_ids: canonicalIds, selection_variants: [game.away_team] }),
    homeRow || createPlaceholderRow(game.home_team, null, 'ML', { canonical_ids: canonicalIds, selection_variants: [game.home_team] }),
  ]
  const activeBooks = new Set([
    ...Object.keys(awayRow?.books || {}),
    ...Object.keys(homeRow?.books || {}),
  ])

  return [{
    market_normalized: 'ML',
    label: 'Moneyline',
    summary: `${activeBooks.size} books`,
    mainRows,
    alternateGroups: orphanRows.length > 0 ? [{
      key: 'orphans',
      label: 'Other selections',
      rows: orphanRows,
    }] : [],
  }]
}

function buildSpreadFamily(markets, game) {
  const awayByHandicap = new Map()
  const homeByHandicap = new Map()
  const orphanRows = []

  markets.filter((market) => market.market_normalized === 'SPREAD').forEach((market) => {
    Object.entries(market.selections).forEach(([selection, books]) => {
      const meta = market.selectionMeta?.[selection] || {}
      const row = createRow(selection, books, market.handicap, 'SPREAD', meta)
      const handicapKey = Number(market.handicap)
      if (selectionMatchesTeam(selection, game.away_team, game.league)) {
        const existing = awayByHandicap.get(handicapKey)
        awayByHandicap.set(handicapKey, existing ? { ...existing, books: mergeBooks(existing.books, books), ...mergeRowMeta(existing, meta) } : row)
      } else if (selectionMatchesTeam(selection, game.home_team, game.league)) {
        const existing = homeByHandicap.get(handicapKey)
        homeByHandicap.set(handicapKey, existing ? { ...existing, books: mergeBooks(existing.books, books), ...mergeRowMeta(existing, meta) } : row)
      } else {
        orphanRows.push(row)
      }
    })
  })

  const seenHome = new Set()
  const pairs = []
  awayByHandicap.forEach((awayRow, awayHandicap) => {
    const homeHandicap = -awayHandicap
    const homeRow = homeByHandicap.get(homeHandicap) || null
    if (homeRow) seenHome.add(homeHandicap)
    pairs.push({ key: `${awayHandicap}|${homeHandicap}`, awayRow, homeRow, awayHandicap, homeHandicap, absLine: Math.abs(Number(awayHandicap) || 0), ...getCoverageStats(awayRow?.books, homeRow?.books) })
  })
  homeByHandicap.forEach((homeRow, homeHandicap) => {
    if (seenHome.has(homeHandicap)) return
    const awayHandicap = -homeHandicap
    const awayRow = awayByHandicap.get(awayHandicap) || null
    pairs.push({ key: `${awayHandicap}|${homeHandicap}`, awayRow, homeRow, awayHandicap, homeHandicap, absLine: Math.abs(Number(homeHandicap) || 0), ...getCoverageStats(awayRow?.books, homeRow?.books) })
  })

  if (pairs.length === 0 && orphanRows.length === 0) return []
  const ranked = [...pairs].sort((left, right) => {
    if (left.bothSidesCount !== right.bothSidesCount) return right.bothSidesCount - left.bothSidesCount
    if (left.totalBooks !== right.totalBooks) return right.totalBooks - left.totalBooks
    return left.absLine - right.absLine
  })

  const main = ranked[0] || null
  const alternateGroups = ranked.slice(1).sort((left, right) => left.absLine - right.absLine).map((pair) => ({
    key: pair.key,
    label: `${pair.awayHandicap > 0 ? '+' : ''}${pair.awayHandicap} / ${pair.homeHandicap > 0 ? '+' : ''}${pair.homeHandicap}`,
    rows: [pair.awayRow, pair.homeRow].filter(Boolean),
  }))

  if (orphanRows.length > 0) {
    alternateGroups.push({ key: 'orphans', label: 'Unpaired selections', rows: orphanRows })
  }

  return [{
    market_normalized: 'SPREAD',
    label: 'Spread',
    summary: main ? `Consensus ${main.absLine} · ${main.bothSidesCount || main.totalBooks} books` : `${orphanRows.length} unpaired rows`,
    mainLabel: main ? `${main.awayHandicap > 0 ? '+' : ''}${main.awayHandicap} / ${main.homeHandicap > 0 ? '+' : ''}${main.homeHandicap}` : null,
    mainRows: main ? [
      main.awayRow || createPlaceholderRow(game.away_team, main.awayHandicap, 'SPREAD', { canonical_ids: game.canonical_ids || [game.canonical_id].filter(Boolean), selection_variants: [game.away_team] }),
      main.homeRow || createPlaceholderRow(game.home_team, main.homeHandicap, 'SPREAD', { canonical_ids: game.canonical_ids || [game.canonical_id].filter(Boolean), selection_variants: [game.home_team] }),
    ] : [],
    alternateGroups,
  }]
}

function buildTotalFamily(markets, game) {
  const lineGroups = new Map()
  const orphanRows = []

  markets.filter((market) => market.market_normalized === 'TOTAL').forEach((market) => {
    Object.entries(market.selections).forEach(([selection, books]) => {
      const side = classifyTotalSelection(selection)
      const handicapKey = Number(market.handicap)
      const meta = market.selectionMeta?.[selection] || {}
      const row = createRow(selection, books, market.handicap, 'TOTAL', meta)
      if (!lineGroups.has(handicapKey)) lineGroups.set(handicapKey, { over: null, under: null })
      const group = lineGroups.get(handicapKey)
      if (side === 'over') group.over = group.over ? { ...group.over, books: mergeBooks(group.over.books, books), ...mergeRowMeta(group.over, meta) } : row
      else if (side === 'under') group.under = group.under ? { ...group.under, books: mergeBooks(group.under.books, books), ...mergeRowMeta(group.under, meta) } : row
      else orphanRows.push(row)
    })
  })

  const pairs = Array.from(lineGroups.entries()).map(([handicap, group]) => ({
    key: `${handicap}`,
    handicap,
    overRow: group.over,
    underRow: group.under,
    absLine: Math.abs(Number(handicap) || 0),
    ...getCoverageStats(group.over?.books, group.under?.books),
  }))

  if (pairs.length === 0 && orphanRows.length === 0) return []
  const ranked = [...pairs].sort((left, right) => {
    if (left.bothSidesCount !== right.bothSidesCount) return right.bothSidesCount - left.bothSidesCount
    if (left.totalBooks !== right.totalBooks) return right.totalBooks - left.totalBooks
    return left.absLine - right.absLine
  })

  const main = ranked[0] || null
  const alternateGroups = ranked.slice(1).sort((left, right) => left.absLine - right.absLine).map((pair) => ({
    key: pair.key,
    label: `${pair.handicap}`,
    rows: [pair.overRow, pair.underRow].filter(Boolean),
  }))

  if (orphanRows.length > 0) {
    alternateGroups.push({ key: 'orphans', label: 'Unpaired selections', rows: orphanRows })
  }

  return [{
    market_normalized: 'TOTAL',
    label: 'Total',
    summary: main ? `Consensus ${main.handicap} · ${main.bothSidesCount || main.totalBooks} books` : `${orphanRows.length} unpaired rows`,
    mainLabel: main ? `${main.handicap}` : null,
    mainRows: main ? [
      main.overRow || createPlaceholderRow('Over', main.handicap, 'TOTAL', { canonical_ids: game.canonical_ids || [game.canonical_id].filter(Boolean), selection_variants: ['Over'] }),
      main.underRow || createPlaceholderRow('Under', main.handicap, 'TOTAL', { canonical_ids: game.canonical_ids || [game.canonical_id].filter(Boolean), selection_variants: ['Under'] }),
    ] : [],
    alternateGroups,
  }]
}

function buildMarketFamilies(markets, game) {
  return [
    ...buildMoneylineFamily(markets, game),
    ...buildSpreadFamily(markets, game),
    ...buildTotalFamily(markets, game),
  ]
}

function renderPreviewRows(family, game) {
  if (!family) return []
  if (family.market_normalized === 'TOTAL') {
    return [
      family.mainRows.find((row) => classifyTotalSelection(row.selection) === 'over'),
      family.mainRows.find((row) => classifyTotalSelection(row.selection) === 'under'),
    ].filter(Boolean)
  }
  const away = family.mainRows.find((row) => selectionMatchesTeam(row.selection, game.away_team, game.league))
  const home = family.mainRows.find((row) => selectionMatchesTeam(row.selection, game.home_team, game.league))
  return [away, home, ...family.mainRows.filter((row) => row !== away && row !== home)].filter(Boolean).slice(0, 2)
}

function getRowStats(row, selectionStats) {
  return selectionStats[`${row.market_normalized}|${row.handicap ?? ''}|${row.selection}`] || {}
}

function OddsTableRow({ row, game, bookmakers, oddsFormat, selectionStats, onChartOpen, variant = 'main' }) {
  const stats = getRowStats(row, selectionStats)
  const selectionLabel = formatSelectionLabel(row, game)
  return (
    <div className={`lot-odds-row ${variant === 'alt' ? 'alt' : ''}`}>
      <div className="lot-odds-sel-col" title={selectionLabel}>{selectionLabel}</div>
      {(row.market_normalized === 'SPREAD' || row.market_normalized === 'TOTAL') && <div className="lot-odds-line-col">{formatLine(row.handicap, row.market_normalized, row.selection)}</div>}
      <div className="lot-odds-chart-col">
        <button className="lot-chart-btn" title="View odds history" onClick={(event) => { event.stopPropagation(); onChartOpen(createChartContext(row, game)) }}>
          <BarChart3 size={12} />
        </button>
      </div>
      <div className="lot-odds-best-col lot-best-cell">
        <span className="lot-best-value">{formatOdds(stats.best, oddsFormat)}</span>
        {stats.bestBook && getBookLogo(stats.bestBook) && <img src={getBookLogo(stats.bestBook)} alt={stats.bestBook} className="lot-best-logo" title={stats.bestBook} onError={(event) => { event.target.style.display = 'none' }} />}
      </div>
      <div className="lot-odds-avg-col lot-avg-cell">{formatOdds(stats.avg, oddsFormat)}</div>
      {bookmakers.map((book) => {
        const value = row.books[book]
        const best = value != null && value === stats.best
        return (
          <div key={book} className={`lot-odds-cell ${value != null ? '' : 'empty'} ${best ? 'best' : ''}`}>
            {value != null ? formatOdds(value, oddsFormat) : '—'}
            {best && value != null && <Star size={8} className="lot-star" />}
          </div>
        )
      })}
    </div>
  )
}

export default function LiveOddsGameCard({ game, bookmakers = [], isExpanded, onToggle, oddsFormat, onChartOpen }) {
  const [showAlternates, setShowAlternates] = useState({})
  const markets = Object.values(game.markets)
  const selectionStats = useMemo(() => buildSelectionStats(markets), [markets])
  const families = useMemo(() => buildMarketFamilies(markets, game), [markets, game])
  const previewFamily = families.find((family) => family.market_normalized === 'ML') || families[0] || null
  const previewFamilyKey = previewFamily?.market_normalized || null
  const familySignature = families.map((family) => family.market_normalized).join('|')
  const previewRows = previewFamily ? renderPreviewRows(previewFamily, game) : []
  const [activeFamily, setActiveFamily] = useState(previewFamily?.market_normalized || 'ML')

  useEffect(() => {
    setActiveFamily((previous) => {
      if (previous && families.some((family) => family.market_normalized === previous)) {
        return previous
      }
      return previewFamilyKey || families[0]?.market_normalized || 'ML'
    })
  }, [game.canonical_id, familySignature, previewFamilyKey])

  const toggleAlternates = (market) => setShowAlternates((prev) => ({ ...prev, [market]: !prev[market] }))
  const currentFamily = families.find((family) => family.market_normalized === activeFamily) || previewFamily

  return (
    <div className={`lot-game ${isExpanded ? 'expanded' : ''} ${game.is_live ? 'lot-game-live' : ''}`}>
      <div className="lot-game-header" onClick={onToggle}>
        <div className="lot-game-teams-col">
          <div className="lot-team-row">{getTeamLogo(game.away_team, game.league) && <img src={getTeamLogo(game.away_team, game.league)} alt="" className="lot-team-logo" onError={(event) => { event.target.style.display = 'none' }} />}<span className="lot-team-name">{getTeamDisplayName(game.away_team, game.league) || game.away_team}</span></div>
          <div className="lot-team-row">{getTeamLogo(game.home_team, game.league) && <img src={getTeamLogo(game.home_team, game.league)} alt="" className="lot-team-logo" onError={(event) => { event.target.style.display = 'none' }} />}<span className="lot-team-name">{getTeamDisplayName(game.home_team, game.league) || game.home_team}</span></div>
        </div>
        <div className="lot-game-status-col">
          {game.is_live ? <div className="lot-live-tag"><span className="lot-live-dot" /><span>{game.period || 'LIVE'}</span></div> : <span className="lot-start-time">{formatStartTime(game.start_time)}</span>}
          <span className="lot-time-until">{!game.is_live && timeUntil(game.start_time)}</span>
        </div>
        <div className="lot-game-score-col">{(game.away_score != null || game.home_score != null) && <><span className="lot-score">{game.away_score ?? '—'}</span><span className="lot-score">{game.home_score ?? '—'}</span></>}</div>
        <div className="lot-game-best-col">
          {previewRows.length === 0 ? <div className="lot-best-preview"><span className="lot-best-val">—</span></div> : previewRows.map((row) => {
            const stats = getRowStats(row, selectionStats)
            return (
              <div key={`${row.market_normalized}|${row.handicap ?? ''}|${row.selection}`} className="lot-best-preview">
                <span className={`lot-best-val ${stats.best != null ? 'lot-green' : ''}`}>{stats.best != null ? formatOdds(stats.best, oddsFormat) : '—'}</span>
                {(row.market_normalized === 'SPREAD' || row.market_normalized === 'TOTAL') && <span className="lot-best-line-meta">{formatLine(row.handicap, row.market_normalized, row.selection)}</span>}
                {stats.bestBook && getBookLogo(stats.bestBook) && <img src={getBookLogo(stats.bestBook)} alt="" className="lot-mini-logo" onError={(event) => { event.target.style.display = 'none' }} />}
              </div>
            )
          })}
        </div>
        <div className="lot-game-expand-col">
          <span className="lot-books-count">{game.bookmakers.length} active</span>
          <span className="lot-books-count lot-preview-market">{previewFamily?.label || 'Odds'}</span>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </div>

      {isExpanded && <div className="lot-expanded">
        {families.length > 1 && (
          <div className="lot-market-tabs">
            {families.map((family) => (
              <button
                key={family.market_normalized}
                type="button"
                className={`lot-market-tab ${activeFamily === family.market_normalized ? 'active' : ''}`}
                onClick={() => setActiveFamily(family.market_normalized)}
              >
                <span>{family.label}</span>
                <span className="lot-market-tab-count">{family.mainRows.length + family.alternateGroups.length}</span>
              </button>
            ))}
          </div>
        )}

        {currentFamily && (() => {
          const family = currentFamily
          const alternatesOpen = Boolean(showAlternates[family.market_normalized])
          return (
            <div key={family.market_normalized} className="lot-market">
              <div className="lot-market-head">
                <div className="lot-market-label">{family.label}</div>
                <div className="lot-market-summary">{family.summary}</div>
              </div>
              <div className="lot-odds-table">
                <div className="lot-odds-header">
                  <div className="lot-odds-sel-col">Selection</div>
                  {(family.market_normalized === 'SPREAD' || family.market_normalized === 'TOTAL') && <div className="lot-odds-line-col">Line</div>}
                  <div className="lot-odds-chart-col"><BarChart3 size={12} /></div>
                  <div className="lot-odds-best-col">Best</div>
                  <div className="lot-odds-avg-col">Avg</div>
                  {bookmakers.map((book) => <div key={book} className="lot-odds-book-col" title={book}>{getBookLogo(book) ? <img src={getBookLogo(book)} alt="" className="lot-book-logo" onError={(event) => { event.target.style.display = 'none'; event.target.nextSibling && (event.target.nextSibling.style.display = 'inline') }} /> : null}<span className="lot-book-name">{book.length > 8 ? `${book.slice(0, 7)}…` : book}</span></div>)}
                </div>

                {(family.market_normalized === 'SPREAD' || family.market_normalized === 'TOTAL') && family.mainLabel && (
                  <div className="lot-alt-group-label lot-main-group-label">
                    Main {family.market_normalized === 'TOTAL' ? 'total' : 'line'}: {family.mainLabel}
                  </div>
                )}

                {family.mainRows.map((row) => <OddsTableRow key={`main-${family.market_normalized}-${row.selection}-${row.handicap ?? ''}`} row={row} game={game} bookmakers={bookmakers} oddsFormat={oddsFormat} selectionStats={selectionStats} onChartOpen={onChartOpen} />)}

                {alternatesOpen && family.alternateGroups.flatMap((group) => [
                  <div key={`label-${family.market_normalized}-${group.key}`} className="lot-alt-group-label">
                    Alt {family.market_normalized === 'TOTAL' ? 'total' : 'line'}: {group.label}
                  </div>,
                  ...group.rows.map((row) => <OddsTableRow key={`alt-${family.market_normalized}-${group.key}-${row.selection}-${row.handicap ?? ''}`} row={row} game={game} bookmakers={bookmakers} oddsFormat={oddsFormat} selectionStats={selectionStats} onChartOpen={onChartOpen} variant="alt" />),
                ])}
              </div>

              {family.alternateGroups.length > 0 && (
                <button type="button" className="lot-alt-toggle" onClick={() => toggleAlternates(family.market_normalized)}>
                  {alternatesOpen ? 'Hide' : 'Show'} {family.alternateGroups.length} alt {family.market_normalized === 'TOTAL' ? 'line' : 'spread'}{family.alternateGroups.length === 1 ? '' : 's'}
                </button>
              )}
            </div>
          )
        })()}
      </div>}
    </div>
  )
}
